"use client";

import { useEffect, useState, type FormEvent } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ArrowRight, Eye, EyeOff, LoaderCircle } from "lucide-react";
import {
  getSignInErrorMessage,
  useAuthSession,
} from "@/hooks/use-auth-session";
import argosLogo from "@/images/png/logo.png";
import { consumeAuthRecoveryMessage } from "@/lib/auth/session";
import { AuthenticationFormTransition } from "@/components/auth/authentication-form-transition";
import { LoginBackground } from "@/components/auth/login-background";

const EXIT_ANIMATION_DURATION_MS = 800;

export default function LoginPage() {
  const router = useRouter();
  const { login, ready, isAuthenticated, session, error } = useAuthSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [redirectTarget, setRedirectTarget] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(() =>
    consumeAuthRecoveryMessage(),
  );

  useEffect(() => {
    if (!ready || !isAuthenticated || isExiting) {
      return;
    }

    const timer = window.setTimeout(() => setIsExiting(true), 0);
    return () => window.clearTimeout(timer);
  }, [
    isAuthenticated,
    isExiting,
    ready,
  ]);

  useEffect(() => {
    if (!isExiting) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const target =
      redirectTarget ??
      (session?.passwordExpired || session?.mustChangePassword
        ? "/mandatory-password-change"
        : "/dashboard");
    const timer = window.setTimeout(
      () => router.replace(target),
      prefersReducedMotion ? 0 : EXIT_ANIMATION_DURATION_MS,
    );

    return () => window.clearTimeout(timer);
  }, [
    isExiting,
    redirectTarget,
    router,
    session?.mustChangePassword,
    session?.passwordExpired,
  ]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setLocalError(null);

    try {
      const nextSession = await login({ email, password });
      setRedirectTarget(
        nextSession.passwordExpired || nextSession.mustChangePassword
          ? "/mandatory-password-change"
          : "/dashboard",
      );
      setIsExiting(true);
    } catch (cause) {
      setLocalError(getSignInErrorMessage(cause));
    } finally {
      setIsSubmitting(false);
    }
  }

  const message = localError ?? error;

  return (
    <LoginBackground>
      <section className="relative z-10 w-full max-w-sm">
        <AuthenticationFormTransition
          isExiting={isExiting}
          className="relative w-full max-w-sm overflow-hidden rounded-lg border border-[rgba(220,234,240,0.36)] bg-[var(--color-surface)] p-6 shadow-2xl shadow-black/20"
        >
          <div className="mb-6 text-center">
            <Image
              src={argosLogo}
              alt="Argos"
              priority
              className="mx-auto h-auto w-55 rounded-lg"
            />
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <label htmlFor="login-email" className="block space-y-1">
              <span className="text-sm font-medium text-[var(--color-text-primary)]">E-mail</span>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-11 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-text-primary)] outline-none transition placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-accent)] focus:ring-2 focus:ring-[rgba(121,214,210,0.28)]"
                placeholder="nome@exemplo.com"
                required
              />
            </label>

            <label htmlFor="login-password" className="block space-y-1">
              <span className="text-sm font-medium text-[var(--color-text-primary)]">Senha</span>
              <span className="relative block">
                <input
                  id="login-password"
                  type={isPasswordVisible ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="h-11 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 pr-11 text-sm text-[var(--color-text-primary)] outline-none transition placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-accent)] focus:ring-2 focus:ring-[rgba(121,214,210,0.28)]"
                  placeholder="Digite sua senha"
                  required
                />
                <button
                  type="button"
                  aria-label={isPasswordVisible ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={isPasswordVisible}
                  onClick={() => setIsPasswordVisible((current) => !current)}
                  className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-soft)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]"
                >
                  {isPasswordVisible ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </span>
            </label>

            {message ? (
              <p role="alert" className="rounded-md border border-[rgba(217,83,79,0.28)] bg-[var(--color-danger-light)] px-3 py-2 text-sm text-[var(--color-danger)]">
                {message}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[var(--color-brand-accent)] px-4 text-sm font-medium text-[var(--color-brand-primary-dark)] transition hover:bg-[var(--color-brand-accent-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              )}
              <span>{isSubmitting ? "Entrando" : "Entrar"}</span>
            </button>
          </form>
        </AuthenticationFormTransition>
      </section>
    </LoginBackground>
  );
}
